import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { DollarSign, ArrowRight, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SiteHeader } from "@/components/Logo";
import { createContest } from "@/lib/contestApi";
import { saveContestSession } from "@/lib/contestStorage";

export default function CreateContest() {
  const nav = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "Palm Breezy Cap Contest",
    host_username: "",
    salary_cap: 50000,
    roster_size: 4,
  });
  const [lockLocal, setLockLocal] = useState("");

  const upd = (k) => (e) =>
    setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.host_username.trim()) {
      toast.error("Pick a display name");
      return;
    }
    setSubmitting(true);
    try {
      let lockIso = null;
      if (lockLocal) {
        const d = new Date(lockLocal);
        if (!isNaN(d.getTime())) lockIso = d.toISOString();
      }
      const res = await createContest({
        name: form.name.trim() || "Palm Breezy Cap Contest",
        host_username: form.host_username.trim(),
        salary_cap: parseInt(form.salary_cap, 10) || 50000,
        roster_size: parseInt(form.roster_size, 10) || 4,
        lock_at: lockIso,
      });
      saveContestSession(res.code, res.participant_id, form.host_username.trim());
      toast.success(`Contest ${res.code} created`);
      nav(`/contest/${res.code}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create contest");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-pbo-cream">
      <SiteHeader />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <div className="mb-10">
          <div className="text-[11px] uppercase tracking-[0.3em] font-semibold text-pbo-gold">
            New contest
          </div>
          <h1 className="font-serif text-pbo-green text-4xl sm:text-5xl mt-2 leading-tight">
            Set the cap. Print the prices.
          </h1>
          <p className="text-pbo-ink/65 mt-3 text-sm max-w-xl">
            Build your salary-cap fantasy contest. You&apos;ll add golfers and prices
            on the next screen — everything is editable until lock.
          </p>
        </div>

        <form
          onSubmit={submit}
          className="bg-white border border-pbo-border rounded-2xl p-6 sm:p-8 space-y-6"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-2 sm:col-span-2">
              <Label className="text-[11px] uppercase tracking-[0.2em] font-semibold text-pbo-muted">
                Contest name
              </Label>
              <Input
                value={form.name}
                onChange={upd("name")}
                placeholder="Palm Breezy Cap Contest"
                className="h-11 bg-pbo-paper"
                data-testid="contest-name-input"
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label className="text-[11px] uppercase tracking-[0.2em] font-semibold text-pbo-muted">
                Your display name (host)
              </Label>
              <Input
                value={form.host_username}
                onChange={upd("host_username")}
                placeholder="e.g. Commish Adam"
                className="h-11 bg-pbo-paper"
                data-testid="contest-host-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-[11px] uppercase tracking-[0.2em] font-semibold text-pbo-muted">
                Salary cap
              </Label>
              <div className="relative">
                <DollarSign className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-pbo-muted" />
                <Input
                  type="number"
                  min={1}
                  step="any"
                  value={form.salary_cap}
                  onChange={upd("salary_cap")}
                  className="h-11 bg-pbo-paper pl-9"
                  data-testid="contest-cap-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-[11px] uppercase tracking-[0.2em] font-semibold text-pbo-muted">
                Roster size (golfers per team)
              </Label>
              <Input
                type="number"
                min={1}
                max={20}
                value={form.roster_size}
                onChange={upd("roster_size")}
                className="h-11 bg-pbo-paper"
                data-testid="contest-roster-input"
              />
            </div>

            <div className="space-y-2 sm:col-span-2">
              <Label className="text-[11px] uppercase tracking-[0.2em] font-semibold text-pbo-muted">
                Lock time (when picks freeze) <span className="lowercase text-pbo-muted/70">— editable later</span>
              </Label>
              <div className="relative">
                <Calendar className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-pbo-muted" />
                <Input
                  type="datetime-local"
                  value={lockLocal}
                  onChange={(e) => setLockLocal(e.target.value)}
                  className="h-11 bg-pbo-paper pl-9"
                  data-testid="contest-lock-input"
                />
              </div>
              <p className="text-[11px] text-pbo-muted">
                Leave blank if you&apos;ll lock it manually. You can edit this any
                time before lock.
              </p>
            </div>
          </div>

          <Button
            type="submit"
            disabled={submitting}
            className="w-full h-14 rounded-full bg-pbo-green text-pbo-cream hover:bg-pbo-green-deep font-semibold text-base"
            data-testid="submit-create-contest-btn"
          >
            {submitting ? "Creating…" : "Create contest"}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </form>
      </div>
    </div>
  );
}
